import { useCallback, useEffect, useState } from "react";
import {
  getClientOverviewBootstrapData,
  getClientOverviewBootstrapError,
  getClientOverviewBootstrapPromise,
} from "../state/clientOverviewBootstrapStore";
import { useClientOverviewBootstrap } from "./useClientOverviewBootstrap";
import { formatPlAmount } from "../utils/formatPlAmount";
import type { ArApAgingCardEntity } from "../../domain/entities/ArApAgingEntity";

const BUCKET_COLORS = ["#22c55e", "#3b82f6", "#d97706", "#ef4444"];
const MAX_BAR_HEIGHT = 176;
const MIN_BAR_HEIGHT = 2;

export interface ArApAgingBucketDisplay {
  label: string;
  amount: string;
  percent: string;
  color: string;
  barHeight: number;
}

export interface ArApAgingTopItemDisplay {
  name: string;
  bucketLabel: string;
  amount: string;
}

export interface ArApAgingCardDisplay {
  title: string;
  totalOutstanding: string;
  overdueAmount: string;
  notOverdueAmount: string;
  buckets: ArApAgingBucketDisplay[];
  alertMessage: string | null;
  topTitle: string;
  topItems: ArApAgingTopItemDisplay[];
  othersSummary: string | null;
  ctaLabel: string;
  ctaRoute: string;
}

export interface ArApAgingDisplayData {
  receivables: ArApAgingCardDisplay;
  payables: ArApAgingCardDisplay;
}

function mapCard(
  card: ArApAgingCardEntity,
  opts: {
    title: string;
    topTitle: string;
    noun: "customers" | "vendors";
    ctaLabel: string;
    ctaRoute: string;
  },
): ArApAgingCardDisplay {
  const notOverdueBucket = {
    label: "Not overdue",
    amount: card.notOverdueAmount,
    percent:
      card.totalOutstandingAmount > 0
        ? Math.round(
            (card.notOverdueAmount / card.totalOutstandingAmount) * 1000,
          ) / 10
        : 0,
  };

  const chartBuckets = [notOverdueBucket, ...card.buckets];

  const maxAmount = chartBuckets.length
    ? Math.max(...chartBuckets.map((bucket) => bucket.amount), 0)
    : 0;

  const buckets = chartBuckets.map((bucket, index) => {
    const color =
      index === 0 ? "#a3a3a3" : (BUCKET_COLORS[index - 1] ?? BUCKET_COLORS[0]);
    let barHeight = 0;
    if (maxAmount > 0 && bucket.amount > 0) {
      const scaled = Math.sqrt(bucket.amount / maxAmount);
      barHeight = Math.round(
        scaled * (MAX_BAR_HEIGHT - MIN_BAR_HEIGHT) + MIN_BAR_HEIGHT,
      );
      if (barHeight < MIN_BAR_HEIGHT) {
        barHeight = MIN_BAR_HEIGHT;
      }
      if (barHeight > MAX_BAR_HEIGHT) {
        barHeight = MAX_BAR_HEIGHT;
      }
    }

    return {
      label: bucket.label,
      amount: formatPlAmount(bucket.amount),
      percent:
        card.totalOutstandingAmount > 0
          ? `${((bucket.amount / card.totalOutstandingAmount) * 100).toFixed(
              1,
            )}%`
          : "0.0%",
      color,
      barHeight,
    };
  });

  const topItems = card.topOutstanding.map((item) => ({
    name: item.name,
    bucketLabel: item.bucketLabel,
    amount: formatPlAmount(item.amount),
  }));

  let othersSummary: string | null = null;
  if (card.othersOutstanding.count > 0 && card.othersOutstanding.amount > 0) {
    othersSummary = `+${card.othersOutstanding.count} more ${opts.noun} · ${formatPlAmount(card.othersOutstanding.amount)} outstanding`;
  }

  return {
    title: opts.title,
    totalOutstanding:
      card.totalOutstandingAmount > 0
        ? `${formatPlAmount(card.totalOutstandingAmount)} outstanding`
        : `${formatPlAmount(0)} outstanding`,
    notOverdueAmount: `${formatPlAmount(card.notOverdueAmount)} not overdue`,
    overdueAmount: `${formatPlAmount(card.overdueAmount)} overdue`,
    buckets,
    alertMessage: card.alertMessage,
    topTitle: opts.topTitle,
    topItems,
    othersSummary,
    ctaLabel: opts.ctaLabel,
    ctaRoute: opts.ctaRoute,
  };
}

const DEFAULT_DATA: ArApAgingDisplayData = {
  receivables: {
    title: "Money owed to you",
    totalOutstanding: "—",
    overdueAmount: "—",
    notOverdueAmount: "—",
    buckets: [],
    alertMessage: null,
    topTitle: "Top Outstanding Customers",
    topItems: [],
    othersSummary: null,
    ctaLabel: "View detailed AR",
    ctaRoute: "/books/ar-aging",
  },
  payables: {
    title: "Money you owe",
    totalOutstanding: "—",
    overdueAmount: "—",
    notOverdueAmount: "—",
    buckets: [],
    alertMessage: null,
    topTitle: "Top Outstanding Vendors",
    topItems: [],
    othersSummary: null,
    ctaLabel: "View detailed AP",
    ctaRoute: "/books/ap-aging",
  },
};

export function useArApAging(): ArApAgingDisplayData & {
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  useClientOverviewBootstrap();

  const [data, setData] = useState<ArApAgingDisplayData>(DEFAULT_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let bootstrapEntity = getClientOverviewBootstrapData();
      if (!bootstrapEntity) {
        const bootstrapPromise = getClientOverviewBootstrapPromise();
        if (bootstrapPromise) {
          bootstrapEntity = await bootstrapPromise;
        }
      }

      const bootstrapError = getClientOverviewBootstrapError();
      if (!bootstrapEntity) {
        throw new Error(
          bootstrapError ?? "Overview bootstrap data unavailable",
        );
      }

      const section = bootstrapEntity.sections.arApAging;
      if (section.status !== "ok" || !section.data) {
        throw new Error(section.error ?? "AR/AP aging unavailable");
      }

      setData({
        receivables: mapCard(section.data.receivables, {
          title: "Money owed to you",
          topTitle: "Top Outstanding Customers",
          noun: "customers",
          ctaLabel: "View detailed AR",
          ctaRoute: "/books/ar-aging",
        }),
        payables: mapCard(section.data.payables, {
          title: "Money you owe",
          topTitle: "Top Outstanding Vendors",
          noun: "vendors",
          ctaLabel: "View detailed AP",
          ctaRoute: "/books/ap-aging",
        }),
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load AR/AP aging";
      setError(message);
      setData(DEFAULT_DATA);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    ...data,
    loading,
    error,
    refetch: load,
  };
}
